import { Input as MTInput } from '@material-tailwind/react';
import * as React from 'react';

import { cn } from '@/shared/utils/cn';

export type LabelInputComponentProps = Omit<
  React.ComponentProps<typeof MTInput>,
  | 'crossOrigin'
  | 'onPointerEnterCapture'
  | 'onPointerLeaveCapture'
  | 'icon'
  | 'onResize'
  | 'onResizeCapture'
>;

export interface LabelInputProps extends LabelInputComponentProps {
  label: string;
  suffixIcon?: React.ReactNode;
  crossOrigin?: string;
  onPointerEnterCapture?: () => void;
  onPointerLeaveCapture?: () => void;
}

const LabelInput = React.forwardRef<HTMLInputElement, LabelInputProps>(
  ({ className, type, suffixIcon, label, ...props }, ref) => {
    const inputProps = {
      ...props,
      type,
      className: cn(
        'placeholder:text-custom-light-grey placeholder:opacity-100 placeholder:text-sm',
        className,
      ),
      label,
      labelProps: {
        className: props.error
          ? 'before:!border-error-red after:!border-error-red'
          : '',
      },
      icon: suffixIcon,
      crossOrigin: props.crossOrigin || '',
      onPointerEnterCapture: props.onPointerEnterCapture || (() => {}),
      onPointerLeaveCapture: props.onPointerLeaveCapture || (() => {}),
    };

    return (
      <MTInput
        ref={ref}
        variant="outlined"
        onResize={undefined}
        onResizeCapture={undefined}
        {...inputProps}
      />
    );
  },
);

LabelInput.displayName = 'Input';

export { LabelInput };

export const InputTheme = {
  defaultProps: {
    color: 'white',
    shrink: true,
  },
  styles: {
    base: {
      input: {
        color: 'text-white',
        disabled: 'disabled:bg-transparent disabled:cursor-not-allowed',
      },
      label: {
        color: 'text-white',
      },
    },
    variants: {
      outlined: {
        base: {
          input: {
            borderWidth: 'border-[0.5px] !focus:border-[0.5px]',
            floated: {
              borderWidth: 'border-[0.5px]',
            },
          },
          label: {
            before: {
              floated: {
                bt: 'before:border-t-[0.5px] peer-focus:before:border-t-[0.5px]',
                bl: 'before:border-l-[0.5px] peer-focus:before:border-l-[0.5px]',
              },
            },
            after: {
              floated: {
                bt: 'after:border-t-[0.5px] peer-focus:after:border-t-[0.5px]',
                br: 'after:border-r-[0.5px] peer-focus:after:border-r-[0.5px]',
              },
            },
          },
        },
        sizes: {
          md: {
            container: {
              height: 'h-[56px]',
            },
            input: {
              fontSize: 'text-sm',
            },
          },
        },
        colors: {
          input: {
            white: {
              borderColor: 'border-white/70',
              borderColorFocused: 'focus:border-white/70',
            },
          },
          label: {
            white: {
              before:
                'before:border-tl-white/70 peer-focus:before:!border-tl-white/70',
              after:
                'after:border-tr-white/70 peer-focus:after:!border-tr-white/70',
            },
          },
        },
        shrink: {
          label: {
            fontSize: '!text-[12px]',
          },
        },
      },
    },
  },
};
